const Pr = 0.299;
const Pg = 0.587;
const Pb = 0.114;

export function rgbToHsp (R, G, B) {
  // Calculate the Perceived brightness.color.js
  const P = Math.sqrt(R * R * Pr + G * G * Pg + B * B * Pb);

  let H, S;

  // Calculate the Hue and Saturation.  (This part works
  // the same way as in the HSV/B and HSL systems???.)
  if (R === G && R === B) {
    H = 0.0;
    S = 0.0;
    return [H, S, P];
  }

  if (R >= G && R >= B) { // R is largest
    if (B >= G) {
      H = 6.0 / 6.0 - 1.0 / 6.0 * (B - G) / (R - G);
      S = 1.0 - G / R;
    } else {
      H = 0.0 / 6.0 + 1.0 / 6.0 * (G - B) / (R - B);
      S = 1.0 - B / R;
    }
  } else if (G >= R && G >= B) { // G is largest
    if (R >= B) {
      H = 6.0 / 6.0 - 1.0 / 6.0 * (R - B) / (G - B);
      S = 1.0 - B / G;
    } else {
      H = 0.0 / 6.0 + 1.0 / 6.0 * (B - R) / (G - R);
      S = 1.0 - R / G;
    }
  } else { // B is largest
    if (G >= R) {
      H = 6.0 / 6.0 - 1.0 / 6.0 * (G - R) / (B - R);
      S = 1.0 - R / B;
    } else {
      H = 0.0 / 6.0 + 1.0 / 6.0 * (R - G) / (B - G);
      S = 1.0 - G / B;
    }
  }

  return [H, S, P];
};

export function hspToRgb (H, S, P) {
  const minOverMax = 1.0 - S;

  let part;
  let R, G, B;

  if (minOverMax > 0.0) {
    if (H < 1.0 / 6.0) { // R>G>B
      H = 6.0 * (H - 0.0 / 6.0);
      part = 1.0 + H * (1.0 / minOverMax - 1.0);
      B = P / Math.sqrt(Pr / minOverMax / minOverMax + Pg * part * part + Pb);
      R = B / minOverMax;
      G = B + H * (R - B);
    } else if (H < 2.0 / 6.0) { // G>R>B
      H = 6.0 * (-H + 2.0 / 6.0);
      part = 1.0 + H * (1.0 / minOverMax - 1.0);
      B = P / Math.sqrt(Pg / minOverMax / minOverMax + Pr * part * part + Pb);
      G = B / minOverMax;
      R = B + H * (G - B);
    } else if (H < 3.0 / 6.0) { // G>B>R
      H = 6.0 * (H - 2.0 / 6.0);
      part = 1.0 + H * (1.0 / minOverMax - 1.0);
      R = P / Math.sqrt(Pg / minOverMax / minOverMax + Pb * part * part + Pr);
      G = R / minOverMax;
      B = R + H * (G - R);
    } else if (H < 4.0 / 6.0) { // B>G>R
      H = 6.0 * (-H + 4.0 / 6.0);
      part = 1.0 + H * (1.0 / minOverMax - 1.0);
      R = P / Math.sqrt(Pb / minOverMax / minOverMax + Pg * part * part + Pr);
      B = R / minOverMax;
      G = R + H * (B - R);
    } else if (H < 5.0 / 6.0) { // B>R>G
      H = 6.0 * (H - 4.0 / 6.0);
      part = 1.0 + H * (1.0 / minOverMax - 1.0);
      G = P / Math.sqrt(Pb / minOverMax / minOverMax + Pr * part * part + Pg);
      B = G / minOverMax;
      R = G + H * (B - G);
    } else { // R>B>G
      H = 6.0 * (-H + 6.0 / 6.0);
      part = 1.0 + H * (1.0 / minOverMax - 1.0);
      G = P / Math.sqrt(Pr / minOverMax / minOverMax + Pb * part * part + Pg);
      R = G / minOverMax;
      B = G + H * (R - G);
    }
  } else {
    if (H < 1.0 / 6.0) { // R>G>B
      H = 6.0 * (H - 0.0 / 6.0);
      R = Math.sqrt(P * P / (Pr + Pg * H * H));
      G = R * H;
      B = 0.0;
    } else if (H < 2.0 / 6.0) { // G>R>B
      H = 6.0 * (-H + 2.0 / 6.0);
      G = Math.sqrt(P * P / (Pg + Pr * H * H));
      R = G * H;
      B = 0.0;
    } else if (H < 3.0 / 6.0) { // G>B>R
      H = 6.0 * (H - 2.0 / 6.0);
      G = Math.sqrt(P * P / (Pg + Pb * H * H));
      B = G * H;
      R = 0.0;
    } else if (H < 4.0 / 6.0) { // B>G>R
      H = 6.0 * (-H + 4.0 / 6.0);
      B = Math.sqrt(P * P / (Pb + Pg * H * H));
      G = B * H;
      R = 0.0;
    } else if (H < 5.0 / 6.0) { // B>R>G
      H = 6.0 * (H - 4.0 / 6.0);
      B = Math.sqrt(P * P / (Pb + Pr * H * H));
      R = B * H;
      G = 0.0;
    } else { // R>B>G
      H = 6.0 * (-H + 6.0 / 6.0);
      R = Math.sqrt(P * P / (Pr + Pb * H * H));
      B = R * H;
      G = 0.0;
    }
  }

  return [R, G, B];
};

export function hexToRgb (hex) {
  if (hex.substring(0, 1) === '#') {
    hex = hex.slice(1);
  }

  const bigint = parseInt(hex, 16);
  const R = (bigint >> 16) & 255;
  const G = (bigint >> 8) & 255;
  const B = bigint & 255;

  return [R, G, B];
};

export function rgbToHex (R, G, B) {
  return '#' + (1 << 24 | R << 16 | G << 8 | B).toString(16).slice(1);
};

export function rgbToCss (rgb) {
  return 'rgb(' + rgb.join(', ') + ')';
};

export function mix (rgbA, rgbB, amount = 0.5) {
  const colorChannelMixer = (colorChannelA, colorChannelB, amountToMix) => {
    const channelA = colorChannelA * amountToMix;
    const channelB = colorChannelB * (1 - amountToMix);

    return parseInt(channelA + channelB);
  };

  const R = colorChannelMixer(rgbA[0], rgbB[0], amount);
  const G = colorChannelMixer(rgbA[1], rgbB[1], amount);
  const B = colorChannelMixer(rgbA[2], rgbB[2], amount);

  return [R, G, B];
};

export function contrast (rgb, light = [0, 0, 0], dark = [255, 255, 255]) {
  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(
    Pr * Math.pow(rgb[0], 2) +
    Pg * Math.pow(rgb[1], 2) +
    Pb * Math.pow(rgb[2], 2)
  );

  if (hsp > 127.5 && luminance(rgb) > 0.25) {
    return light;
  } else {
    return dark;
  }
};

export function luminance (rgb) {
  // http://www.w3.org/TR/WCAG20/#relativeluminancedef
  const lum = [];
  for (let i = 0; i < rgb.length; i++) {
    const chan = rgb[i] / 255;
    lum[i] = (chan <= 0.03928) ? chan / 12.92 : Math.pow(((chan + 0.055) / 1.055), 2.4);
  }

  return 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
}
